import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import _ from 'lodash-es'
import { ColumnTypeOverride } from '../../models/column-type-override.model'
import {
  ColumnFilterModel,
  ColumnTypeOverrideContext,
  FilterEditorModel,
  FilterEditorRowModel,
} from '../../models/filter-editor.model'
import { FilterModel, FilterState } from '../../models/filter.model'
import { retypedColumnFilterModel } from '../../utils/agg-info-filter-utils'
import {
  createFilterRowModel,
  createFilterState,
} from '../../utils/filter-editor-utils'

@Component({
  selector: 'hs-filter-editor[editorModel]',
  templateUrl: './filter-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterEditorComponent implements OnInit {
  private _nextId = 0

  @Input() editorModel!: FilterEditorModel
  @Output() editorModelChange = new EventEmitter<FilterEditorModel>()

  /**
   * Opt-in for the per-column comparison-type picker (PVAL-26423). OFF by default, so the editor is
   * byte-for-byte what it was for every consumer that does not ask for it.
   *
   * Threaded from the host grid's {@link FilterModalService} through `FilterModalComponent`, rather
   * than inferred from the row model, deliberately: `typeOverride` being present on a column is a
   * statement about the CATALOG (this field's type is an unreliable placeholder), while this flag is a
   * statement about the GRID (this grid can actually act on a retype — it has a /query request for the
   * chosen type to travel on). Clearwater's client-side grid satisfies the first and not the second,
   * and offering the picker there would be a control that silently does nothing.
   */
  @Input() enableColumnTypeOverride = false

  columnFilters: ColumnFilterModel[] = []
  columnFiltersAvailable: ColumnFilterModel[] = []
  filterModel: FilterModel = {}
  rowModels: FilterEditorRowModel[] = []

  /**
   * The comparison-type override to render a picker for on this row, or `null` for no picker.
   *
   * Both gates in one place so the template cannot satisfy only one of them: the host grid must have
   * opted in, AND the row's column must actually carry an override (which only an AGGREGATE_INFO
   * column does). Returning the override itself rather than a boolean lets the template alias it and
   * stay type-safe about reading `.type`.
   */
  columnTypeOverrideFor(
    rowModel: FilterEditorRowModel,
  ): ColumnTypeOverrideContext | null {
    if (!this.enableColumnTypeOverride) return null
    return rowModel.column?.typeOverride ?? null
  }

  /**
   * The user picked a different comparison type for a column whose type they own (an AGGREGATE_INFO
   * column — see `agg-info-filter-utils`). Only reachable when a type picker is projected into the
   * row, which only happens for such a column.
   *
   * Deliberately does NOT write to {@link ColumnTypeOverrideService} here — picking a type, like
   * picking an operator or typing a value, is an in-progress edit. Only Apply commits it (see
   * `FilterModalComponent.onApply`, which reads the chosen type back off `editorModel.columnFilters`),
   * the same point everything else in this modal already commits at. Writing immediately would
   * rebuild the grid's ColDefs — and reset whatever the user has since done in the Column Manager —
   * before they have even clicked Apply, or even if they Cancel.
   *
   * Two things still happen immediately, because they are local, in-modal-only concerns:
   *
   *  1. This column's filter model is re-derived for the new type, so the row offers the NEW type's
   *     operators rather than the old one's.
   *  2. The row is replaced by a fresh one (new `id`, so `trackByRowId` re-creates the component)
   *     carrying no operator or value. The old predicate belonged to the OLD type: `contains "12/31"`
   *     is not a meaningful date predicate, and leaving it in place is one of the ways a retry after a
   *     400 INVALID_FIELD_TYPE fails identically.
   */
  onColumnTypeChange(index: number, type: ColumnTypeOverride): void {
    const column = this.rowModels[index]?.column
    const typeOverride = column?.typeOverride
    if (!column || !typeOverride || typeOverride.type === type) return

    const retyped = retypedColumnFilterModel(column, {
      scope: typeOverride.scope,
      type,
    })
    this.columnFilters = this.columnFilters.map(columnFilter =>
      columnFilter.field === retyped.field ? retyped : columnFilter,
    )
    this.rowModels = this.rowModels.map((row, i) =>
      i === index ? { id: this.freshId(), column: retyped } : row,
    )
    this.updateFilterModel()
  }

  async ngOnInit(): Promise<void> {
    this._nextId = this.editorModel?.nextId ?? 0
    this.columnFilters = this.editorModel?.columnFilters ?? []
    this.filterModel = this.editorModel?.filterModel ?? {}
    if (this.editorModel?.rowModels) {
      this.rowModels = this.editorModel.rowModels
    } else {
      await this.buildRowModels(this.filterModel)
    }
    this.setAvailableColumns()
  }

  setAvailableColumns() {
    this.columnFiltersAvailable = this.columnFilters.filter(ele => {
      return !this.rowModels.some(f => {
        return f.column?.field === ele.field
      })
    })
  }

  async buildRowModels(model: { [key: string]: FilterState }): Promise<void> {
    const fields = Object.keys(model)
    const rows = await Promise.all(
      fields.map(async field => {
        const rowModel = await this.createRowModel(
          field,
          this.filterModel[field],
        )
        return rowModel
      }),
    )
    this.rowModels = [...rows, this.createEmptyRowModel()]
    this.updateFilterEditorModel()
  }

  addNewFilterRow() {
    this.rowModels = [...this.rowModels, this.createEmptyRowModel()]
    this.updateFilterModel()
  }

  deleteFilterRow(rowIndex: number) {
    this.rowModels = this.rowModels.filter((_r, i) => i !== rowIndex)
    this.updateFilterModel()
  }

  updateRowModel(rowModel: FilterEditorRowModel, index: number) {
    // Create a new rowModels array with the updated index
    this.rowModels = _.setWith(_.cloneDeep(this.rowModels), index, rowModel)
    this.updateFilterModel()
  }

  updateFilterModel() {
    // rebuild filterModel from filter state list
    this.filterModel = this.rowModels.reduce<FilterModel>((model, row) => {
      const field = row.column?.field
      const filterState = createFilterState(row)
      if (!field || !filterState) {
        return model
      }

      model[field] = filterState
      return model
    }, {})

    this.updateFilterEditorModel()
  }

  updateFilterEditorModel() {
    this.setAvailableColumns()
    this.editorModelChange.emit({
      columnFilters: this.columnFilters,
      filterModel: this.filterModel,
      rowModels: this.rowModels,
      nextId: this._nextId,
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackByRowId(_index: number, row: any): any {
    return row.id
  }

  private createEmptyRowModel(): FilterEditorRowModel {
    return {
      id: this.freshId(),
    }
  }

  private async createRowModel(
    field: string,
    filterState: FilterState,
  ): Promise<FilterEditorRowModel> {
    const rowModel = await createFilterRowModel(
      this.columnFilters,
      this.freshId(),
      field,
      filterState,
    )
    return rowModel
  }

  private freshId(): number {
    return this._nextId++
  }
}
